<template>
  <div class="flex-1 flex flex-col md:flex-row min-h-0 overflow-hidden">
    <!-- Left: basic fields -->
    <div class="md:w-[40%] overflow-y-auto p-6 border-b md:border-b-0 md:border-r border-gray-200">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-lg font-semibold text-gray-900">Edit Project</h2>
        <button @click="store.closeEditPane()" class="text-gray-500 hover:text-gray-700" title="Close">
          <X :size="20" />
        </button>
      </div>

      <form @submit.prevent="save" class="space-y-4">
        <div>
          <label class="text-sm font-medium text-gray-800 mb-1 block">Name</label>
          <input v-model="form.name" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-teal-500" />
        </div>

        <div v-if="error" class="text-sm text-red-600">{{ error }}</div>

        <div class="flex gap-3 pt-2">
          <button type="submit" :disabled="saving" class="px-4 py-2 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:opacity-50">
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
          <button type="button" @click="store.closeEditPane()" class="px-4 py-2 border border-gray-300 text-sm rounded text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </form>

      <!-- CMS Connection -->
      <div class="mt-8 border-t border-gray-200 pt-6">
        <label class="text-sm font-medium text-gray-800 mb-1 block">CMS Connection</label>
        <p class="text-sm text-gray-600 mb-2">Link a CMS to publish content directly.</p>

        <select
          v-model="selectedCmsId"
          @change="handleCmsChange"
          class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500"
        >
          <option :value="null">None</option>
          <option v-for="conn in store.cmsConnections.value" :key="conn.id" :value="conn.id">
            {{ conn.name }} ({{ conn.base_url }})
          </option>
        </select>

        <div v-if="selectedCmsId" class="mt-2 flex items-center gap-2">
          <button
            @click="testConnection"
            :disabled="testingConnection"
            class="text-sm px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >{{ testingConnection ? 'Testing...' : 'Test connection' }}</button>
          <button
            @click="deleteConnection"
            :disabled="deletingConnection"
            class="text-sm px-3 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
          >{{ deletingConnection ? 'Deleting...' : 'Delete' }}</button>
          <span v-if="connectionTestResult" class="text-sm" :class="connectionTestResult === 'ok' ? 'text-green-600' : 'text-red-600'">
            {{ connectionTestResult === 'ok' ? 'Connected' : connectionTestResult }}
          </span>
        </div>

        <!-- New connection form -->
        <button
          v-if="!showNewConnForm"
          @click="showNewConnForm = true"
          class="mt-2 text-sm text-teal-600 hover:text-teal-700"
        >+ New connection</button>

        <div v-if="showNewConnForm" class="mt-3 p-3 border border-gray-200 rounded bg-gray-50 space-y-2">
          <input v-model="newConn.name" type="text" placeholder="Connection name" class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500" />
          <input v-model="newConn.base_url" type="text" placeholder="https://cms.example.com" class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500" />
          <input v-model="newConn.api_token" type="password" placeholder="API token" class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500" />
          <div class="flex gap-2">
            <button
              @click="createConnection"
              :disabled="creatingConnection || !newConn.name || !newConn.base_url || !newConn.api_token"
              class="text-sm px-3 py-1 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
            >{{ creatingConnection ? 'Creating...' : 'Create' }}</button>
            <button @click="showNewConnForm = false" class="text-sm px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
          </div>
          <p v-if="newConnError" class="text-sm text-red-600">{{ newConnError }}</p>
        </div>
      </div>

      <!-- Topics -->
      <div class="mt-8 border-t border-gray-200 pt-6">
        <label class="text-sm font-medium text-gray-800 mb-1 block">Topics</label>
        <p class="text-sm text-gray-600 mb-2">One per line. Used to categorize content.</p>
        <textarea
          v-model="topicsText"
          @blur="syncTopics"
          rows="4"
          placeholder="Content Marketing&#10;Product Updates&#10;Customer Stories&#10;Industry Research"
          class="w-full text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500 resize-y"
        ></textarea>
        <p v-if="topicStatus" class="text-sm text-teal-600 mt-1">{{ topicStatus }}</p>
      </div>
    </div>

    <!-- Right: knowledge items -->
    <div class="flex-1 overflow-y-auto p-6 bg-gray-50/50">
      <div class="flex items-center justify-between mb-1">
        <label class="text-sm font-medium text-gray-800">Knowledge items</label>
        <button @click="addKI" class="text-sm bg-teal-600 text-white rounded px-2 py-1 hover:bg-teal-700">+ Add</button>
      </div>
      <p class="text-sm text-gray-600 mb-3">Reference material for the AI — research, guidelines, facts, brand rules. One item per block. Auto-saves on blur.</p>

      <div v-if="knowledgeItems.length === 0" class="text-sm text-gray-600 italic">
        No knowledge items yet.
      </div>
      <div v-for="(ki, idx) in knowledgeItems" :key="ki.id || ki._tempId" class="mb-3 border border-gray-200 rounded bg-white">
        <div class="flex items-start">
          <textarea
            v-model="ki.content"
            @blur="saveKI(ki)"
            rows="6"
            placeholder="Paste reference material &#x2014; brand guidelines, research findings, product specs, competitor analysis, key statistics, or any facts the AI should draw from..."
            class="flex-1 text-sm text-gray-900 border-0 rounded-l px-3 py-2 focus:outline-none resize-y"
          ></textarea>
          <button @click="deleteKI(ki, idx)" class="px-2 py-2 text-gray-400 hover:text-red-500 flex-shrink-0" title="Delete">
            <Trash2 :size="16" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, inject, onMounted } from 'vue'
import { X, Trash2 } from 'lucide-vue-next'
import { useApi } from '../../composables/useApi'

const store = inject('workbench')
const api = useApi()
const saving = ref(false)
const error = ref(null)

const form = reactive({ name: '' })

const topicsText = ref('')
const topicStatus = ref(null)
const topicsFromServer = ref([])

const knowledgeItems = ref([])
let tempIdCounter = 0

// CMS connection state
const selectedCmsId = ref(null)
const showNewConnForm = ref(false)
const newConn = reactive({ name: '', base_url: '', api_token: '' })
const creatingConnection = ref(false)
const newConnError = ref(null)
const testingConnection = ref(false)
const connectionTestResult = ref(null)
const deletingConnection = ref(false)

onMounted(async () => {
  const p = store.currentProject.value
  if (!p) return
  form.name = p.name || ''
  selectedCmsId.value = p.cms_connection_id || null

  try {
    const [t, k] = await Promise.all([
      api.get(`/api/projects/${p.id}/topics`),
      api.get(`/api/projects/${p.id}/knowledge_items`),
    ])
    topicsFromServer.value = t
    topicsText.value = t.map(topic => topic.name).join('\n')
    knowledgeItems.value = k
  } catch (e) {
    console.error('Failed to load project data', e)
  }
})

async function save() {
  if (!store.currentProject.value) return
  saving.value = true
  error.value = null
  try {
    const updated = await api.patch(`/api/projects/${store.currentProject.value.id}`, {
      project: { ...form }
    })
    Object.assign(store.currentProject.value, updated)
    await store.loadProjects()
    store.closeEditPane()
  } catch (e) {
    error.value = e.message || 'Failed to save'
  } finally {
    saving.value = false
  }
}

async function syncTopics() {
  const projectId = store.currentProject.value?.id
  if (!projectId) return

  const newNames = topicsText.value.split('\n').map(s => s.trim()).filter(Boolean)
  const existing = topicsFromServer.value

  for (const t of existing) {
    if (!newNames.includes(t.name)) {
      try { await api.delete(`/api/projects/${projectId}/topics/${t.id}`) } catch (e) { /* ignore */ }
    }
  }

  const existingNames = existing.map(t => t.name)
  for (const name of newNames) {
    if (!existingNames.includes(name)) {
      try { await api.post(`/api/projects/${projectId}/topics`, { topic: { name } }) } catch (e) { /* ignore */ }
    }
  }

  try {
    topicsFromServer.value = await api.get(`/api/projects/${projectId}/topics`)
    topicStatus.value = 'Saved'
    setTimeout(() => topicStatus.value = null, 2000)
  } catch (e) { /* ignore */ }
}

function addKI() {
  knowledgeItems.value.push({ _tempId: ++tempIdCounter, content: '', _new: true })
}

async function saveKI(ki) {
  const projectId = store.currentProject.value?.id
  if (!projectId || !ki.content?.trim()) return
  try {
    if (ki._new || !ki.id) {
      const created = await api.post(`/api/projects/${projectId}/knowledge_items`, {
        knowledge_item: { title: '—', content: ki.content }
      })
      ki.id = created.id
      ki._new = false
      delete ki._tempId
    } else {
      await api.patch(`/api/projects/${projectId}/knowledge_items/${ki.id}`, {
        knowledge_item: { content: ki.content }
      })
    }
  } catch (e) {
    console.error('Failed to save knowledge item', e)
  }
}

async function deleteKI(ki, idx) {
  const projectId = store.currentProject.value?.id
  if (ki.id && projectId) {
    try { await api.delete(`/api/projects/${projectId}/knowledge_items/${ki.id}`) } catch (e) { /* ignore */ }
  }
  knowledgeItems.value.splice(idx, 1)
}

// CMS connection handlers
async function handleCmsChange() {
  connectionTestResult.value = null
  await store.setCmsConnection(selectedCmsId.value)
}

async function testConnection() {
  if (!selectedCmsId.value) return
  testingConnection.value = true
  connectionTestResult.value = null
  try {
    const result = await store.testCmsConnection(selectedCmsId.value)
    connectionTestResult.value = result.status === 'ok' ? 'ok' : (result.message || 'Connection failed')
  } finally {
    testingConnection.value = false
  }
}

async function deleteConnection() {
  if (!selectedCmsId.value) return
  if (!confirm('Delete this CMS connection? It will be unlinked from all projects using it.')) return
  deletingConnection.value = true
  try {
    await store.deleteCmsConnection(selectedCmsId.value)
    selectedCmsId.value = null
    connectionTestResult.value = null
  } finally {
    deletingConnection.value = false
  }
}

async function createConnection() {
  creatingConnection.value = true
  newConnError.value = null
  try {
    const conn = await store.createCmsConnection({ ...newConn })
    selectedCmsId.value = conn.id
    await store.setCmsConnection(conn.id)
    showNewConnForm.value = false
    newConn.name = ''
    newConn.base_url = ''
    newConn.api_token = ''
  } catch (e) {
    newConnError.value = e.message || 'Failed to create connection'
  } finally {
    creatingConnection.value = false
  }
}
</script>
